import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import moment from "moment";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import CustomModal from "../../packages/CustomModal";
import { FEEDBACK_TYPES } from "../../packages/DPFeedbackIconTooltip/DPFeedbackIconTooltip.jsx";
import WPCounterInput from "../../packages/WPCounterInput/WPCounterInput.jsx";
import WPDropdown from "../../packages/WPDropdown";
import WPInput from "../../packages/WPInput";
import WPTertiaryButton from "../../packages/WPTertiaryButton";
import WPTextArea from "../../packages/WPTextArea/WPTextArea.jsx";

import { DatePickerConfig } from "../../components/DatePopover/DateTimeConfig";
import DupontDatePicker from "../../components/DatePopover/DupontDatePicker";
import Dropdown from "../../components/Dropdown";
import DupontCheckbox from "../../components/DupontCheckbox";
import DupontRadio from "../../components/DupontRadio";
import TrainTabs from "../../components/TrainTabs/index.js";
import ElementSelectionDialog from "./ElementSelectionDialog";

import {
  CustomModalButtons,
  STAGE_DEFAULTS,
  TECHNOLOGIES,
} from "../../constants/global.constants.js";

import { NewBadgeIcon, WrongIcon } from "../../assets/icons";

import CustomTooltip from "../../common/styles/components/tooltip/CustomTooltip";

import {
  getPermissionIdsByPermissionNames,
  hasExpiringSoonLicenseForPermissionIds,
} from "../../utils/LicenseUtils";

import { fetchRONFElementsWithCache } from "../../network/ronfElementApiCache.js";

import "./index.scss";

const logROConfigDebug = (...args) => {
  console.log("[ROConfiguration]", ...args);
};

const ValidationTabTitle = ({ label, isInvalid, tooltipText }) => (
  <span className="config-tab-title">
    <span className={isInvalid ? "config-tab-title--invalid" : ""}>
      {label}
    </span>
    {isInvalid && (
      <CustomTooltip text={tooltipText} placement="top" maxWidth="220px">
        <span className="config-tab-error-icon" aria-hidden="true">
          <WrongIcon />
        </span>
      </CustomTooltip>
    )}
  </span>
);

const isNonEmptySelectedElement = (selectedElement) =>
  String(selectedElement ?? "").trim() !== "";

const hasCompleteIsdSelection = (selectedElements, elementsPerVessel) =>
  Number.isFinite(elementsPerVessel) &&
  elementsPerVessel >= 1 &&
  elementsPerVessel <= 8 &&
  Array.isArray(selectedElements) &&
  selectedElements.length === elementsPerVessel &&
  selectedElements.every(isNonEmptySelectedElement);

const hasInvalidRoSelection = (stage, enforceRequiredAll) => {
  const roValue = stage.ro_nf_element_used;

  if (stage.is_isd) {
    if (!Array.isArray(roValue)) {
      return true;
    }

    const elementsPerVessel = Number(stage.num_element_per_pressure_vessel);
    if (
      !Number.isFinite(elementsPerVessel) ||
      elementsPerVessel < 1 ||
      elementsPerVessel > 8
    ) {
      return Boolean(enforceRequiredAll);
    }

    return !hasCompleteIsdSelection(roValue, elementsPerVessel);
  }

  return typeof roValue !== "string" || roValue.trim() === "";
};

const RoConfiguration = ({
  trains,
  setTrains,
  totalNumberOfElements,
  setTotalNumberOfElements,
  stageValidationSucessfull,
  setStageValidationSucessfull,
  noOfTrains,
  RONFElements,
  roValidationContext,
  setMandatoryComponentsStatus,
  waterTypeOptions,
  roReconcileTrigger = 0,
}) => {
  const { t } = useTranslation();
  const [traintabs, setTraintabs] = useState([]);
  const [selectedTrain, setSelectedTrain] = useState(0); // trains are 0-indexed, but displayed as 1-indexed to the user
  const [selectedTrainPage, setSelectedTrainPage] = useState(null);
  const [isNonDuPontElement, setIsNonDuPontElement] = useState(false);
  const [selectedTrainRONFElements, setSelectedTrainRONFElements] = useState(
    [],
  );

  const isTrainInvalidLive = (trainData, enforceRequiredAll = false) => {
    if (!trainData?.passes?.length) {
      return true;
    }

    return trainData.passes.some((pass) => {
      if (!pass?.stages?.length) return true;

      return pass.stages.some((stage) => {
        if (!stage || typeof stage !== "object") {
          return enforceRequiredAll;
        }

        const hasPressureKey = Object.prototype.hasOwnProperty.call(
          stage,
          "num_pressure_vessels",
        );
        const hasElementsKey = Object.prototype.hasOwnProperty.call(
          stage,
          "num_element_per_pressure_vessel",
        );
        const hasRoKey = Object.prototype.hasOwnProperty.call(
          stage,
          "ro_nf_element_used",
        );

        const shouldCheckPressure = enforceRequiredAll || hasPressureKey;
        if (shouldCheckPressure) {
          const pressureValue = stage.num_pressure_vessels;
          if (pressureValue === undefined || pressureValue === "") return true;
          const pressureNumber = Number(pressureValue);
          if (
            !Number.isFinite(pressureNumber) ||
            pressureNumber < 1 ||
            pressureNumber > 100000
          ) {
            return true;
          }
        }

        const shouldCheckElements = enforceRequiredAll || hasElementsKey;
        if (shouldCheckElements) {
          const elementsValue = stage.num_element_per_pressure_vessel;
          if (elementsValue === undefined || elementsValue === "") return true;
          const elementsNumber = Number(elementsValue);
          if (
            !Number.isFinite(elementsNumber) ||
            elementsNumber < 1 ||
            elementsNumber > 8
          ) {
            return true;
          }
        }

        const shouldCheckRo = enforceRequiredAll || hasRoKey || stage?.is_isd;
        if (shouldCheckRo) {
          if (hasInvalidRoSelection(stage, enforceRequiredAll)) {
            return true;
          }
        }

        return false;
      });
    });
  };

  const markAsChanged = () => {
    if (setMandatoryComponentsStatus) {
      setMandatoryComponentsStatus((prev) => ({
        ...prev,
        roConfiguration: { ...prev.roConfiguration, changed: true },
      }));
    }
  };

  useEffect(() => {
    if (Array.isArray(trains) && trains.length > 0) {
      setIsNonDuPontElement(!trains[selectedTrain]?.is_dupont_element);
    }
  }, [selectedTrain, trains]);

  const selectedTrainWaterType = useMemo(() => {
    return trains?.[selectedTrain]?.water_type;
  }, [trains, selectedTrain]);

  const selectedTrainWaterTypeError = useMemo(() => {
    if (!roValidationContext?.show) {
      return "";
    }

    return selectedTrainWaterType ? "" : t("addNewPlant.requiredFieldMessage");
  }, [roValidationContext, selectedTrainWaterType, t]);

  useEffect(() => {
    if (!selectedTrainWaterType) {
      setSelectedTrainRONFElements([]);
      return;
    }

    let isCancelled = false;

    const fetchTrainLevelRONFElements = async () => {
      try {
        const cachedOrFetchedElements = await fetchRONFElementsWithCache(
          selectedTrainWaterType,
        );

        if (!isCancelled) {
          setSelectedTrainRONFElements(cachedOrFetchedElements);
          setTrains((prevTrains) => {
            const updatedTrains = [...prevTrains];
            if (!updatedTrains[selectedTrain]) {
              return prevTrains;
            }
            updatedTrains[selectedTrain] = {
              ...updatedTrains[selectedTrain],
              ro_nf_elements: cachedOrFetchedElements,
            };

            return updatedTrains;
          });
        }
      } catch (error) {
        if (!isCancelled) {
          setSelectedTrainRONFElements([]);
          setTrains((prevTrains) => {
            const updatedTrains = [...prevTrains];
            if (!updatedTrains[selectedTrain]) {
              return prevTrains;
            }
            updatedTrains[selectedTrain] = {
              ...updatedTrains[selectedTrain],
              ro_nf_elements: [],
            };

            return updatedTrains;
          });
        }
      }
    };

    fetchTrainLevelRONFElements();

    return () => {
      isCancelled = true;
    };
  }, [selectedTrain, selectedTrainWaterType]);

  useEffect(() => {
    const fetchAllTrainsRONFElements = async () => {
      const allTrainsRONFElements = await Promise.all(
        trains.map(async (trainData) => {
          if (trainData?.water_type) {
            try {
              const elements = await fetchRONFElementsWithCache(
                trainData.water_type,
              );
              return elements;
            } catch (error) {
              return [];
            }
          }
        }),
      );

      setTrains((prevTrains) => {
        const updatedTrains = [...prevTrains];
        updatedTrains.forEach((train, index) => {
          train.ro_nf_elements = allTrainsRONFElements[index] || [];
        });

        return updatedTrains;
      });
    };

    fetchAllTrainsRONFElements();
  }, []);

  useEffect(() => {
    const tabs = [];
    if (noOfTrains === "" || noOfTrains === undefined || noOfTrains === "0") {
      return;
    }

    const nextTrainObj = (Array.isArray(trains) ? trains : [])
      .filter(
        (train) =>
          train &&
          Number.isFinite(Number(train.train_id)) &&
          Number(train.train_id) > 0,
      )
      .sort((a, b) => Number(a.train_id) - Number(b.train_id));
    let hasStructuralChanges = false;

    if (nextTrainObj.length > Number(noOfTrains)) {
      nextTrainObj.splice(Number(noOfTrains));
      hasStructuralChanges = true;
    }

    const start = nextTrainObj.length;
    for (let i = start + 1; i <= parseInt(noOfTrains); i++) {
      const newTrain = {
        train_id: i,
        passes: [
          {
            pass_id: 1,
            stages: [
              {
                stage_id: 1,
                num_pressure_vessels: STAGE_DEFAULTS.NUM_PRESSURE_VESSELS,
                num_element_per_pressure_vessel:
                  STAGE_DEFAULTS.NUM_ELEMENTS_PER_PV,
              },
            ],
          },
        ],
        is_dupont_element: true,
        case_notes: null,
      };
      nextTrainObj.push(newTrain);
      hasStructuralChanges = true;
    }

    for (let i = 1; i <= parseInt(noOfTrains); i++) {
      const trainData = nextTrainObj?.[i - 1];
      const isTrainInvalid = isTrainInvalidLive(
        trainData,
        Boolean(roValidationContext?.show),
      );

      tabs.push({
        id: `ro-configuration-train-tab-${i}`,
        train: String(i),
        title: (
          <ValidationTabTitle
            label={`${t("ValueStrings.train")} ${i}`}
            isInvalid={isTrainInvalid}
            tooltipText={t("addNewPlant.trainConfigDetailsMissingTooltip")}
          />
        ),
        page: (
          <PassComponent
            train_id={i}
            t={t}
            trains={nextTrainObj}
            setTrains={setTrains}
            setTotalNumberOfElements={setTotalNumberOfElements}
            stageValidationSucessfull={stageValidationSucessfull}
            setStageValidationSucessfull={setStageValidationSucessfull}
            isNonDuPontElement={isNonDuPontElement}
            selectedTrainRONFElements={selectedTrainRONFElements}
            showValidationBadges={roValidationContext?.show}
            markAsChanged={markAsChanged}
          />
        ),
      });
    }

    if (hasStructuralChanges) {
      setTrains([...nextTrainObj]);
    }

    setTraintabs(tabs);
    if (tabs && tabs.length && tabs.length > selectedTrain) {
      setSelectedTrainPage(tabs[selectedTrain]?.page);
    } else {
      setSelectedTrain(0);
      setSelectedTrainPage(tabs[0]?.page);
    }
  }, [
    noOfTrains,
    trains,
    stageValidationSucessfull,
    t,
    roValidationContext,
    selectedTrainRONFElements,
  ]);

  const clearRONFElementUsedFields = (updatedTrains, trainIdToReset) => {
    const sourceTrains = Array.isArray(updatedTrains)
      ? updatedTrains
      : Array.isArray(trains)
        ? trains
        : [];
    let hasChanges = false;

    const newTrainObj = sourceTrains.map((train, trainIndex) => {
      const shouldProcessTrain =
        trainIdToReset === undefined || trainIndex === trainIdToReset;

      if (!shouldProcessTrain || !Array.isArray(train?.passes)) {
        return train;
      }

      let trainChanged = false;

      const updatedPasses = train.passes.map((pass) => {
        if (!Array.isArray(pass?.stages)) {
          return pass;
        }

        let passChanged = false;

        const updatedStages = pass.stages.map((stage) => {
          if (stage?.ro_nf_element_used === undefined) {
            return stage;
          }

          passChanged = true;
          hasChanges = true;
          return {
            ...stage,
            ro_nf_element_used: undefined,
          };
        });

        if (!passChanged) {
          return pass;
        }

        trainChanged = true;
        return {
          ...pass,
          stages: updatedStages,
        };
      });

      if (!trainChanged) {
        return train;
      }

      return {
        ...train,
        passes: updatedPasses,
      };
    });

    if (hasChanges) {
      setTrains(newTrainObj);
    }
  };

  const onTrainTabSelected = (trainValue) => {
    const selectedTrainIndex = Number(trainValue) - 1;
    if (selectedTrainIndex < 0 || !traintabs[selectedTrainIndex]) {
      logROConfigDebug(`Invalid train tab selection: ${trainValue}`);
      return;
    }

    const selectedTrainData = trains?.[selectedTrainIndex];
    logROConfigDebug(
      `Opened Train ${selectedTrainData?.train_id} (passes: ${
        selectedTrainData?.passes?.length || 0
      })`,
    );

    setSelectedTrainPage(traintabs[selectedTrainIndex].page);
    setSelectedTrain(selectedTrainIndex);
  };

  const handleIsNonDuPontElementChange = (isNonDuPont) => {
    logROConfigDebug(
      `Train ${trains?.[selectedTrain]?.train_id}: mode changed to ${
        isNonDuPont ? "Non-DuPont" : "DuPont"
      }`,
    );
    setIsNonDuPontElement(isNonDuPont);
    const updatedTrains = trains.map((train, idx) =>
      idx === selectedTrain
        ? { ...train, is_dupont_element: !isNonDuPont }
        : train,
    );
    setTrains(updatedTrains);
    clearRONFElementUsedFields(updatedTrains, selectedTrain);
    markAsChanged();
  };

  const reconcileInvalidElementSelections = (
    effectiveRONFElements,
    trainIdToValidate,
  ) => {
    if (
      !Array.isArray(effectiveRONFElements) ||
      effectiveRONFElements.length === 0
    ) {
      return;
    }

    const elementsArr = effectiveRONFElements.map((element) => element?.value);

    setTrains((prevTrains) => {
      if (!Array.isArray(prevTrains) || prevTrains.length === 0) {
        return prevTrains;
      }

      let hasChanges = false;

      const updatedTrains = prevTrains.map((train, trainIndex) => {
        const shouldProcessTrain =
          trainIdToValidate === undefined || trainIndex === trainIdToValidate;

        if (!shouldProcessTrain || train?.is_dupont_element === false) {
          return train;
        }

        let trainChanged = false;

        const updatedPasses = (train.passes || []).map((pass) => {
          if (!Array.isArray(pass?.stages)) {
            return pass;
          }

          let passChanged = false;

          const updatedStages = pass.stages.map((stage) => {
            const currentSelection = stage?.ro_nf_element_used;

            if (currentSelection === undefined || currentSelection === "") {
              return stage;
            }

            if (stage?.is_isd) {
              const selectedElements = Array.isArray(currentSelection)
                ? currentSelection
                : [];
              const normalizedElements = selectedElements.map((element) =>
                elementsArr.includes(element) ? element : undefined,
              );

              const hasInvalidSelections = selectedElements.some(
                (element) => !elementsArr.includes(element),
              );

              if (!hasInvalidSelections) {
                return stage;
              }

              passChanged = true;
              hasChanges = true;
              return {
                ...stage,
                ro_nf_element_used: normalizedElements,
              };
            }

            if (
              typeof currentSelection === "string" &&
              elementsArr.includes(currentSelection)
            ) {
              return stage;
            }

            passChanged = true;
            hasChanges = true;
            return {
              ...stage,
              ro_nf_element_used: undefined,
            };
          });

          if (!passChanged) {
            return pass;
          }

          trainChanged = true;
          return {
            ...pass,
            stages: updatedStages,
          };
        });

        if (!trainChanged) {
          return train;
        }

        return {
          ...train,
          passes: updatedPasses,
        };
      });

      if (hasChanges) {
        markAsChanged();
        return updatedTrains;
      }

      return prevTrains;
    });
  };

  const handleTrainWaterTypeChange = (value) => {
    const updatedTrains = [...trains];
    updatedTrains.forEach((train, index) => {
      if (index === selectedTrain) {
        updatedTrains[index] = {
          ...updatedTrains[index],
          water_type: value,
        };
      }
    });

    setTrains(updatedTrains);
  };

  // Validate the selected train's RONF elements whenever they change or the reconciliation trigger is activated during import wave config
  useEffect(() => {
    reconcileInvalidElementSelections(selectedTrainRONFElements, selectedTrain);
  }, [selectedTrainRONFElements, roReconcileTrigger, trains]);

  // Validate all trains' RONF elements whenever the global RONF elements change or the reconciliation trigger is activated during import wave config
  useEffect(() => {
    reconcileInvalidElementSelections(RONFElements);
  }, [RONFElements, roReconcileTrigger, trains]);

  const selectedTrainPassCount =
    trains?.[selectedTrain]?.passes?.length > 0
      ? trains[selectedTrain].passes.length
      : 1;

  const handleTrainPassCountChange = (event) => {
    const nextPassCount = Math.max(
      1,
      Math.floor(Number(event?.target?.value ?? event)),
    );

    if (!Number.isFinite(nextPassCount)) {
      return;
    }

    const updatedTrains = Array.isArray(trains) ? [...trains] : [];
    const currentTrain = updatedTrains[selectedTrain];

    if (!currentTrain) {
      return;
    }

    const nextTrain = {
      ...currentTrain,
      passes: Array.isArray(currentTrain.passes)
        ? [...currentTrain.passes]
        : [],
    };

    const currentPasses = [...nextTrain.passes];

    if (currentPasses.length > nextPassCount) {
      nextTrain.passes = currentPasses.slice(0, nextPassCount);
    } else if (currentPasses.length < nextPassCount) {
      nextTrain.passes = [...currentPasses];
      for (let i = currentPasses.length + 1; i <= nextPassCount; i++) {
        nextTrain.passes.push({
          pass_id: i,
          stages: [
            {
              stage_id: 1,
              num_pressure_vessels: STAGE_DEFAULTS.NUM_PRESSURE_VESSELS,
              num_element_per_pressure_vessel:
                STAGE_DEFAULTS.NUM_ELEMENTS_PER_PV,
            },
          ],
        });
      }
    } else {
      return;
    }

    nextTrain.passes = nextTrain.passes.map((pass, index) => ({
      ...pass,
      pass_id: index + 1,
    }));

    updatedTrains[selectedTrain] = nextTrain;

    setTrains(updatedTrains);
    markAsChanged();
  };

  const handleTrainNotesChange = (value) => {
    setTrains((prevTrains) => {
      if (!Array.isArray(prevTrains) || !prevTrains[selectedTrain]) {
        return prevTrains;
      }

      const updatedTrains = [...prevTrains];
      updatedTrains[selectedTrain] = {
        ...updatedTrains[selectedTrain],
        case_notes: value,
      };

      return updatedTrains;
    });

    markAsChanged();
  };

  return (
    <>
      {/*Removed Number of trains input box - can be used when editing trains functionality introduces */}
      {/* <div className="flex-between outline-box">
        <span className="description-text">
          {t("addNewPlant.noOfTrains")}
          <span style={{ color: "red" }}> *</span>
        </span>
        <InputBox
          id="no-of-trains"
          name="trains"
          value={noOfTrains}
          customClass="custom-input-width "
          placeholder={t("addNewPlant.noOfTrains")}
          disabled
          hideClearBtn
        />
      </div> */}
      {traintabs && (
        <>
          <TrainTabs
            trainTabs={traintabs}
            selectedTrain={selectedTrain + 1}
            onTrainChange={onTrainTabSelected}
          />
          <div className="train-fields-container">
            <div className="train-fields-row">
              <WPDropdown
                id="add-plant-water-type"
                name="waterType"
                label={
                  <span className="new-badge-inline-wrapper">
                    {t("addNewPlant.waterType")}
                    <NewBadgeIcon className="new-badge" />
                  </span>
                }
                placeholder={t("addNewPlant.waterType")}
                value={trains?.[selectedTrain]?.water_type || ""}
                onChange={(selectedOption) =>
                  handleTrainWaterTypeChange(selectedOption?.value || "")
                }
                options={waterTypeOptions}
                required
                isError={Boolean(selectedTrainWaterTypeError)}
                helperText={selectedTrainWaterTypeError}
              />
              <WPCounterInput
                id="add-plant-no-of-passes"
                name="passes"
                value={selectedTrainPassCount}
                onChange={handleTrainPassCountChange}
                ranges={{ hard: { min: 1, max: 3 } }}
                label={t("addNewPlant.noOfPasses")}
                hideRangeAlways
                required
              />
            </div>
            <DupontCheckbox
              labelText={t("addNewPlant.nonDupontElement")}
              checked={isNonDuPontElement}
              onChange={handleIsNonDuPontElementChange}
            />
          </div>
          <div>{selectedTrainPage}</div>
          <div className="flex-jc-end padding-top-20 align-items-center total-elements-row">
            {t("addNewPlant.totalNoOfElements")}
            <div className="total-elements-input">
              <WPInput
                id="add-plant-total-elements"
                name="totalNoOfElements"
                label=""
                value={totalNumberOfElements}
                placeholder={t(`addNewPlant.totalNoOfElements`)}
                disabled
              />
            </div>
          </div>

          <WPTextArea
            id={`train-notes-${selectedTrain + 1}`}
            value={trains?.[selectedTrain]?.case_notes || ""}
            label={t("addNewPlant.trainNotes")}
            placeholder={t("addNewPlant.trainNotesPlaceholder")}
            rows={3}
            required={false}
            onChange={handleTrainNotesChange}
            maxCount={500}
            customClassName="train-notes-textarea"
          />
        </>
      )}
    </>
  );
};

export default RoConfiguration;

export const PassComponent = ({
  train_id,
  t,
  trains,
  setTrains,
  setTotalNumberOfElements,
  stageValidationSucessfull,
  setStageValidationSucessfull,
  isNonDuPontElement,
  selectedTrainRONFElements,
  showValidationBadges,
  markAsChanged = () => {},
}) => {
  train_id -= 1;
  const [passtabs, setPasstabs] = useState([]);
  const [selectedPass, setSelectedPass] = useState(0);
  const [selectedPassPage, setSelectedPassPage] = useState(null);

  const isPassInvalidLive = (passData, enforceRequiredAll = false) => {
    if (!passData?.stages?.length) return true;

    return passData.stages.some((stage) => {
      if (!stage || typeof stage !== "object") return enforceRequiredAll;

      const hasPressureKey = Object.prototype.hasOwnProperty.call(
        stage,
        "num_pressure_vessels",
      );
      const hasElementsKey = Object.prototype.hasOwnProperty.call(
        stage,
        "num_element_per_pressure_vessel",
      );
      const hasRoKey = Object.prototype.hasOwnProperty.call(
        stage,
        "ro_nf_element_used",
      );

      const shouldCheckPressure = enforceRequiredAll || hasPressureKey;
      if (shouldCheckPressure) {
        const pressureValue = stage.num_pressure_vessels;
        if (pressureValue === undefined || pressureValue === "") return true;
        const pressureNumber = Number(pressureValue);
        if (
          !Number.isFinite(pressureNumber) ||
          pressureNumber < 1 ||
          pressureNumber > 100000
        ) {
          return true;
        }
      }

      const shouldCheckElements = enforceRequiredAll || hasElementsKey;
      if (shouldCheckElements) {
        const elementsValue = stage.num_element_per_pressure_vessel;
        if (elementsValue === undefined || elementsValue === "") return true;
        const elementsNumber = Number(elementsValue);
        if (
          !Number.isFinite(elementsNumber) ||
          elementsNumber < 1 ||
          elementsNumber > 8
        ) {
          return true;
        }
      }

      const shouldCheckRo = enforceRequiredAll || hasRoKey || stage?.is_isd;
      if (shouldCheckRo) {
        if (hasInvalidRoSelection(stage, enforceRequiredAll)) {
          return true;
        }
      }

      return false;
    });
  };

  const passNo =
    trains[train_id]?.passes?.length <= 0
      ? 1
      : trains[train_id]?.passes?.length;

  useEffect(() => {
    const tabs = [];
    for (let i = 1; i <= passNo; i++) {
      const passData = trains?.[train_id]?.passes?.[i - 1];
      const isPassInvalid = isPassInvalidLive(
        passData,
        Boolean(showValidationBadges),
      );

      tabs.push({
        id: `ro-configuration-pass-tab-${train_id + 1}-${i}`,
        train: String(i),
        label: `${t("addNewPlant.pass")} ${i}`,
        tabval: (
          <ValidationTabTitle
            label={`${t("addNewPlant.pass")} ${i}`}
            isInvalid={isPassInvalid}
            tooltipText={t("addNewPlant.passConfigDetailsMissingTooltip")}
          />
        ),
        page: (
          <StageComponent
            train_id={train_id}
            pass_id={i}
            t={t}
            trains={trains}
            setTrains={setTrains}
            setTotalNumberOfElements={setTotalNumberOfElements}
            stageValidationSucessfull={stageValidationSucessfull}
            setStageValidationSucessfull={setStageValidationSucessfull}
            isNonDuPontElement={isNonDuPontElement}
            selectedTrainRONFElements={selectedTrainRONFElements}
            markAsChanged={markAsChanged}
          />
        ),
      });
    }

    setPasstabs(tabs);

    if (tabs.length === 0) {
      setSelectedPassPage(null);
      setSelectedPass(0);
      return;
    }

    if (tabs[selectedPass]) {
      setSelectedPassPage(tabs[selectedPass].page);
    } else {
      setSelectedPass(0);
      setSelectedPassPage(tabs[0].page);
    }
  }, [
    trains,
    train_id,
    passNo,
    stageValidationSucessfull,
    t,
    showValidationBadges,
    isNonDuPontElement,
    selectedTrainRONFElements,
  ]);
  const onPassTabSelected = (passValue) => {
    const selectedPassIndex = Number(passValue) - 1;
    if (selectedPassIndex < 0 || !passtabs[selectedPassIndex]) {
      return;
    }

    setSelectedPassPage(passtabs[selectedPassIndex].page);
    setSelectedPass(selectedPassIndex);
  };

  return (
    <>
      {passtabs && (
        <>
          <TrainTabs
            trainTabs={passtabs.map((tab) => ({
              ...tab,
              title: tab.tabval,
            }))}
            selectedTrain={selectedPass + 1}
            onTrainChange={onPassTabSelected}
          />
          <div>{selectedPassPage}</div>
        </>
      )}
    </>
  );
};

const stageOptions = () => {
  return ["1", "2", "3", "4", "5", "6", "7", "8"];
};

export const StageComponent = ({
  train_id,
  pass_id,
  t,
  trains,
  setTrains,
  setTotalNumberOfElements,
  stageValidationSucessfull,
  isNonDuPontElement,
  selectedTrainRONFElements,
  markAsChanged = () => {},
}) => {
  const userLicenses = useSelector((state) => state.userLicenses || []);
  const stageOption = stageOptions();
  const pass_id_index = pass_id - 1;
  const passIdValue = pass_id;

  const [inputVal, setInputVal] = useState([]);
  const [showISDModal, setShowISDModal] = useState([]);
  const [isISDConfirmEnabled, setIsISDConfirmEnabled] = useState(false);
  const [replaceOrphanConfirm, setReplaceOrphanConfirm] = useState({
    show: false,
    index: null,
    value: "",
  });
  const [expiringSoonDialog, setExpiringSoonDialog] = useState({
    show: false,
    index: null,
  });
  const stageNo =
    trains[train_id]?.passes[pass_id_index]?.stages?.length <= 0
      ? 1
      : trains[train_id]?.passes[pass_id_index]?.stages?.length;

  const buildStagesForCount = (stages, desiredCount) => {
    const existingStages = Array.isArray(stages) ? stages : [];
    const limitedStages = existingStages.slice(0, desiredCount);
    const nextStages = limitedStages.map((stage, index) => ({
      ...stage,
      stage_id: stage?.stage_id ?? index + 1,
      num_pressure_vessels:
        stage?.num_pressure_vessels ?? STAGE_DEFAULTS.NUM_PRESSURE_VESSELS,
      num_element_per_pressure_vessel:
        stage?.num_element_per_pressure_vessel ??
        STAGE_DEFAULTS.NUM_ELEMENTS_PER_PV,
    }));

    for (let i = limitedStages.length + 1; i <= desiredCount; i++) {
      nextStages.push({
        stage_id: i,
        num_pressure_vessels: STAGE_DEFAULTS.NUM_PRESSURE_VESSELS,
        num_element_per_pressure_vessel: STAGE_DEFAULTS.NUM_ELEMENTS_PER_PV,
      });
    }

    return nextStages;
  };

  const updatePassStages = (nextStages) => {
    const updatedTrains = Array.isArray(trains) ? [...trains] : [];
    const currentTrain = updatedTrains[train_id];

    if (!currentTrain || !Array.isArray(currentTrain.passes)) {
      return null;
    }

    const updatedPasses = [...currentTrain.passes];
    const currentPass = updatedPasses[pass_id_index] || {};

    updatedPasses[pass_id_index] = {
      ...currentPass,
      stages: nextStages,
    };

    updatedTrains[train_id] = {
      ...currentTrain,
      passes: updatedPasses,
    };

    return updatedTrains;
  };

  const updateStageAtIndex = (stageIndex, stageUpdater) => {
    const updatedTrains = Array.isArray(trains) ? [...trains] : [];
    const currentTrain = updatedTrains[train_id];

    if (!currentTrain || !Array.isArray(currentTrain.passes)) {
      return null;
    }

    const updatedPasses = [...currentTrain.passes];
    const currentPass = updatedPasses[pass_id_index];

    if (!currentPass || !Array.isArray(currentPass.stages)) {
      return null;
    }

    const updatedStages = [...currentPass.stages];
    const previousStage = updatedStages[stageIndex] || {};
    updatedStages[stageIndex] = stageUpdater(previousStage);

    updatedPasses[pass_id_index] = {
      ...currentPass,
      stages: updatedStages,
    };

    updatedTrains[train_id] = {
      ...currentTrain,
      passes: updatedPasses,
    };

    return updatedTrains;
  };

  useEffect(() => {
    if (Array.isArray(trains)) {
      setInputVal([...trains]);
      setTotalNumberOfElements(getTotalNumber(trains));
    }
  }, [trains, t]);

  useEffect(() => {
    const desiredCount = parseInt(stageNo);
    const existingStages = trains[train_id]?.passes[pass_id_index]?.stages;
    const nextStages = buildStagesForCount(existingStages, desiredCount);
    const updatedTrains = updatePassStages(nextStages);
    if (updatedTrains) {
      setTrains(updatedTrains);
    }
    setShowISDModal((prev) => {
      const arr = Array(desiredCount).fill(false);
      if (prev && Array.isArray(prev)) {
        for (let i = 0; i < arr.length && i < prev.length; i++) {
          arr[i] = prev[i];
        }
      }
      return arr;
    });
  }, []);

  const onStageChanged = (id, value) => {
    const start = trains[train_id]?.passes[pass_id_index]?.stages?.length || 1;
    const desiredCount = parseInt(value);
    const existingStages = trains[train_id]?.passes[pass_id_index]?.stages;
    const nextStages = buildStagesForCount(existingStages, desiredCount);
    const updatedTrains = updatePassStages(nextStages);

    logROConfigDebug(
      `Train ${
        train_id + 1
      }, Pass ${passIdValue}: stage count ${start} -> ${Number(value)}`,
    );
    markAsChanged();
    if (updatedTrains) {
      setTrains(updatedTrains);
    }
  };

  const onStageInputChanged = (stageId, value, name) => {
    if (name === "installation_year") {
      value = value ? moment(value).format("YYYY-MM-DD") : "";
    }
    const trainId = train_id + 1;
    const updatedTrains = updateStageAtIndex(stageId, (prevStage) => {
      let is_isd = prevStage?.is_isd;

      if (name === "is_isd") {
        is_isd = value === "isd";
      }

      const updatedStage = {
        ...prevStage,
        [name]: value,
        is_isd,
      };

      if (name === "num_element_per_pressure_vessel" && prevStage?.is_isd) {
        const oldCount = prevStage.num_element_per_pressure_vessel;
        const newCount = value;
        if (
          oldCount !== newCount &&
          Array.isArray(prevStage.ro_nf_element_used)
        ) {
          updatedStage.ro_nf_element_used = [];
          logROConfigDebug(
            `Train ${trainId}, Pass ${passIdValue}, Stage ${
              stageId + 1
            }: ISD elements cleared after element count change (${oldCount} -> ${newCount})`,
          );
        }
      }

      if (name === "is_isd") {
        logROConfigDebug(
          `Train ${trainId}, Pass ${passIdValue}, Stage ${
            stageId + 1
          }: type set to ${updatedStage.is_isd ? "ISD" : "Single"}`,
        );
      }

      return updatedStage;
    });

    if (!updatedTrains) {
      return;
    }

    markAsChanged();
    setTrains(updatedTrains);
    setInputVal([...updatedTrains]);

    setTotalNumberOfElements(getTotalNumber(updatedTrains));
  };

  const getInputVal = (key, index) => {
    if (
      Object.keys(inputVal).length > 0 &&
      inputVal[train_id] &&
      inputVal[train_id].passes[pass_id_index]
    ) {
      const id = inputVal[train_id].passes[pass_id_index].stages[index];
      return id && id[key] ? id[key] : "";
    } else return "";
  };

  const getTotalNumber = (trains) => {
    const stageValue = [];

    for (const train of trains) {
      for (const pass of train.passes) {
        for (const stage of pass.stages) {
          const { NUM_PRESSURE_VESSELS, NUM_ELEMENTS_PER_PV } = STAGE_DEFAULTS;
          const num_pressure_vessels =
            stage.num_pressure_vessels ?? NUM_PRESSURE_VESSELS;
          const num_element_per_pressure_vessel =
            stage.num_element_per_pressure_vessel ?? NUM_ELEMENTS_PER_PV;

          stageValue.push({
            num_pressure_vessels,
            num_element_per_pressure_vessel,
          });
        }
      }
    }
    return calculateCumulativeSum(stageValue);
  };
  const calculateCumulativeSum = (data) => {
    let cumulativeSum = 0;
    for (const element of data) {
      const numPressureVessels = parseInt(element.num_pressure_vessels);
      const numElementPerPressureVessel = parseInt(
        element.num_element_per_pressure_vessel,
      );
      const product = numPressureVessels * numElementPerPressureVessel;
      cumulativeSum += product;
    }
    const sum = isNaN(cumulativeSum) ? "" : cumulativeSum;
    return sum;
  };
  const getErrors = (key, index) => {
    const stageData =
      trains?.[train_id]?.passes?.[pass_id_index]?.stages?.[index] || {};
    const fieldValue = stageData?.[key];
    const isFieldTouched = Object.prototype.hasOwnProperty.call(stageData, key);

    if (key === "ro_nf_element_used") {
      const isMissingElement = stageData?.is_isd
        ? !hasCompleteIsdSelection(
            fieldValue,
            Number(stageData?.num_element_per_pressure_vessel),
          )
        : typeof fieldValue !== "string" || fieldValue.trim() === "";

      if (isMissingElement && (!stageValidationSucessfull || isFieldTouched)) {
        return t("addNewPlant.requiredFieldMessage");
      }

      return "";
    }

    if (
      (fieldValue === undefined || fieldValue === "") &&
      (!stageValidationSucessfull || isFieldTouched)
    ) {
      return t("addNewPlant.requiredFieldMessage");
    }

    const numericValue = Number(fieldValue);
    if (fieldValue !== undefined && fieldValue !== "") {
      if (
        key === "num_pressure_vessels" &&
        (numericValue < 1 || numericValue > 100000)
      ) {
        return "Range: 1-100000";
      }

      if (
        key === "num_element_per_pressure_vessel" &&
        (numericValue < 1 || numericValue > 8)
      ) {
        return "Range: 1-8";
      }
    }

    return "";
  };

  const handleSingleElementChange = (stageIndex, selectedOption) => {
    const newValue = selectedOption?.value || "";
    const currentValue = getInputVal("ro_nf_element_used", stageIndex) || "";
    const isCurrentValueOrphan =
      !!currentValue &&
      !selectedTrainRONFElements?.some((opt) => opt?.value === currentValue);

    if (isCurrentValueOrphan && newValue && newValue !== currentValue) {
      setReplaceOrphanConfirm({
        show: true,
        index: stageIndex,
        value: newValue,
      });
      return;
    }

    onStageInputChanged(stageIndex, newValue, "ro_nf_element_used");

    if (
      newValue &&
      hasExpiringSoonLicenseForPermissionIds(
        userLicenses,
        elementPermissionsByValue[newValue] || [],
      )
    ) {
      setExpiringSoonDialog({
        show: true,
        index: stageIndex,
      });
    }
  };

  const handleReplaceOrphanModalClose = (actionId) => {
    const id = actionId?.target?.id || actionId;
    if (
      id === CustomModalButtons.CONFIRM &&
      replaceOrphanConfirm.index !== null
    ) {
      onStageInputChanged(
        replaceOrphanConfirm.index,
        replaceOrphanConfirm.value,
        "ro_nf_element_used",
      );
    }

    setReplaceOrphanConfirm({ show: false, index: null, value: "" });
  };

  const handleExpiringSoonDialogClose = () => {
    setExpiringSoonDialog({ show: false, index: null });
  };

  const elementPermissionsByValue = useMemo(() => {
    return (
      Array.isArray(selectedTrainRONFElements) ? selectedTrainRONFElements : []
    )
      .filter((option) => option?.value)
      .reduce((acc, option) => {
        acc[option.value] = getPermissionIdsByPermissionNames(
          option?.permissions || [],
        );
        return acc;
      }, {});
  }, [selectedTrainRONFElements]);

  return (
    <>
      <div className="flex-between description-text outline-box no-bottom-border">
        <span className="description-text">
          {t("addNewPlant.noOfstage")}
          <span style={{ color: "red" }}> *</span>
        </span>
        <div className="custom-input-width">
          <Dropdown
            id="no-of-stages"
            placeholder={t("addNewPlant.stage")}
            value={stageNo.toString()}
            onChange={(value) => onStageChanged(pass_id, value)}
            options={stageOption}
          />
        </div>
      </div>
      <div className="flex-between outline-box stage-container no-top-radius">
        <div className="labels-section description-text marginTop30 ">
          <div className="padding-top-20">
            {t("addNewPlant.noOfVessels")}
            <span style={{ color: "red" }}> *</span>
          </div>
          <div className="padding-top-20">
            {t("addNewPlant.noOfElementsVessels")}
            <span style={{ color: "red" }}> *</span>
          </div>
          <div className="label-align-with-radio">
            {t("addNewPlant.ro/nfElements")}
            <span style={{ color: "red" }}> *</span>
          </div>
          <div className="padding-top-30">
            {t("addNewPlant.installationYear")}
          </div>
        </div>
        <div className="stage-inputs-section">
          {Array.from({
            length:
              trains[train_id]?.passes[pass_id_index]?.stages?.length || 0,
          }).map((_, index) => (
            <div className="margin-left-stage stage-Input">
              {(() => {
                const pressureError = getErrors("num_pressure_vessels", index);
                const elementsError = getErrors(
                  "num_element_per_pressure_vessel",
                  index,
                );
                const roElementError = getErrors("ro_nf_element_used", index);
                const isElementsRangeError =
                  elementsError &&
                  elementsError !== t("addNewPlant.requiredFieldMessage");
                const selectedElementValue =
                  getInputVal("ro_nf_element_used", index) || "";
                const hasMissingSelectedOption =
                  selectedElementValue &&
                  !selectedTrainRONFElements?.some(
                    (opt) => opt?.value === selectedElementValue,
                  );
                const hasExpiringSoonWarning =
                  selectedElementValue &&
                  !hasMissingSelectedOption &&
                  hasExpiringSoonLicenseForPermissionIds(
                    userLicenses,
                    elementPermissionsByValue[selectedElementValue] || [],
                  );

                return (
                  <>
                    <div className="teal-text marginBtm20">{`${t(
                      "addNewPlant.stage",
                    )} ${index + 1}`}</div>
                    <div>
                      <div className="stage-field-with-tooltip">
                        {pressureError && (
                          <div className="stage-field-side-tooltip">
                            {pressureError}
                          </div>
                        )}
                        <WPInput
                          key={`${pass_id}num_pressure_vessels${index}`}
                          type="number"
                          allowDecimals={false}
                          roundOff={null}
                          name="num_pressure_vessels"
                          value={getInputVal("num_pressure_vessels", index)}
                          placeholder={t("addNewPlant.noOfVessels")}
                          onChange={(event) =>
                            onStageInputChanged(
                              index,
                              event?.target?.value,
                              "num_pressure_vessels",
                            )
                          }
                          maxLength={6}
                          externalError={pressureError}
                          hideRangeAlways
                        />
                      </div>

                      <div className="stage-field-with-tooltip stage-field-spacing-top">
                        {elementsError && (
                          <div
                            className={`stage-field-side-tooltip ${
                              isElementsRangeError
                                ? "stage-field-side-tooltip--wide"
                                : ""
                            }`}
                          >
                            {elementsError}
                          </div>
                        )}
                        <WPInput
                          key={`${pass_id}num_element_per_pressure_vessel ${index}`}
                          type="number"
                          allowDecimals={false}
                          roundOff={null}
                          name="num_element_per_pressure_vessel"
                          value={getInputVal(
                            "num_element_per_pressure_vessel",
                            index,
                          )}
                          placeholder={t(
                            "addNewPlant.noOfElementsVesselsPlaceholder",
                          )}
                          onChange={(event) =>
                            onStageInputChanged(
                              index,
                              event?.target?.value,
                              "num_element_per_pressure_vessel",
                            )
                          }
                          maxLength={3}
                          externalError={elementsError}
                          hideRangeAlways
                        />
                      </div>

                      {isNonDuPontElement ? (
                        <WPInput
                          key={`${pass_id}ro_nf_element_used${index}`}
                          name="ro_nf_element_used"
                          value={getInputVal("ro_nf_element_used", index)}
                          placeholder={t("addNewPlant.ro/nfElements")}
                          onChange={(e) => {
                            onStageInputChanged(
                              index,
                              e.target.value,
                              "ro_nf_element_used",
                            );
                          }}
                          customValidation={(value) => value !== " "}
                          requiredMsg={roElementError}
                          externalError={roElementError}
                        />
                      ) : (
                        <>
                          <div
                            className="element-type-radio"
                            onChange={(e) => {
                              const value = e.target.value;
                              const trainId = train_id + 1;
                              const trainIndex = trains.findIndex(
                                (trainItem) => trainItem.train_id === trainId,
                              );
                              if (trainIndex !== -1) {
                                const passIndex = trains[
                                  trainIndex
                                ].passes.findIndex(
                                  (passItem) =>
                                    passItem.pass_id === passIdValue,
                                );
                                if (passIndex !== -1) {
                                  let updatedStage = {
                                    ...trains[trainIndex]?.passes?.[passIndex]
                                      ?.stages?.[index],
                                    is_isd: value === "isd",
                                  };
                                  if (value === "isd") {
                                    updatedStage.ro_nf_element_used = [];
                                  } else {
                                    updatedStage.ro_nf_element_used = "";
                                  }
                                  const updatedTrains = updateStageAtIndex(
                                    index,
                                    () => updatedStage,
                                  );
                                  logROConfigDebug(
                                    `Train ${trainId}, Pass ${passIdValue}, Stage ${
                                      index + 1
                                    }: switched to ${value.toUpperCase()}`,
                                  );
                                  if (updatedTrains) {
                                    markAsChanged();
                                    setTrains(updatedTrains);
                                    setInputVal([...updatedTrains]);
                                  }
                                }
                              }
                            }}
                          >
                            <DupontRadio
                              text={t("addNewPlant.single")}
                              value="single"
                              name={`stage-type-${pass_id}-${index}`}
                              checked={getInputVal("is_isd", index) !== true}
                            />
                            <DupontRadio
                              text={TECHNOLOGIES.ISD}
                              value="isd"
                              name={`stage-type-${pass_id}-${index}`}
                              checked={getInputVal("is_isd", index) === true}
                            />
                          </div>
                          {getInputVal("is_isd", index) === true ? (
                            <>
                              <WPTertiaryButton
                                label={t("addNewPlant.editISD")}
                                variant="outlined"
                                disabled={
                                  !getInputVal(
                                    "num_element_per_pressure_vessel",
                                    index,
                                  ) ||
                                  isNaN(
                                    Number(
                                      getInputVal(
                                        "num_element_per_pressure_vessel",
                                        index,
                                      ),
                                    ),
                                  ) ||
                                  Number(
                                    getInputVal(
                                      "num_element_per_pressure_vessel",
                                      index,
                                    ),
                                  ) < 1 ||
                                  Number(
                                    getInputVal(
                                      "num_element_per_pressure_vessel",
                                      index,
                                    ),
                                  ) > 8
                                }
                                onClick={() => {
                                  setIsISDConfirmEnabled(false);
                                  setShowISDModal((prev) => {
                                    const newState = [...(prev || [])];
                                    newState[index] = true;
                                    return newState;
                                  });
                                }}
                                className="stage-Input btn-full-width"
                              />

                              {showISDModal &&
                                showISDModal[index] &&
                                (() => {
                                  if (!window._elementDialogRefs)
                                    window._elementDialogRefs = {};
                                  const dialogRefKey = `${train_id}-${pass_id_index}-${index}`;
                                  if (
                                    !window._elementDialogRefs[dialogRefKey]
                                  ) {
                                    window._elementDialogRefs[dialogRefKey] =
                                      React.createRef();
                                  }
                                  const elementDialogRef =
                                    window._elementDialogRefs[dialogRefKey];
                                  return (
                                    <CustomModal
                                      header={t(
                                        "addNewPlant.internalStageDesign",
                                      )}
                                      show={true}
                                      confirmBtn={t("ValueStrings.confirm")}
                                      size="small"
                                      confirmBtnDisabled={!isISDConfirmEnabled}
                                      isLoading={window.__isdModalLoading}
                                      onModalClose={async ({ target }) => {
                                        if (target.id === "modal-confirm-btn") {
                                          if (
                                            elementDialogRef.current &&
                                            elementDialogRef.current.allSelected
                                          ) {
                                            const elements =
                                              elementDialogRef.current.elements;
                                            const updatedTrains =
                                              updateStageAtIndex(
                                                index,
                                                (previousStage) => ({
                                                  ...previousStage,
                                                  ro_nf_element_used: elements,
                                                }),
                                              );
                                            logROConfigDebug(
                                              `Train ${
                                                train_id + 1
                                              }, Pass ${passIdValue}, Stage ${
                                                index + 1
                                              }: ISD elements saved [${elements.join(
                                                ", ",
                                              )}]`,
                                            );
                                            if (updatedTrains) {
                                              markAsChanged();
                                              setTrains(updatedTrains);
                                            }
                                            try {
                                              window.__isdModalLoading = true;
                                              const params =
                                                new URLSearchParams(
                                                  window.location.search,
                                                );
                                              const isEdit =
                                                params.get("isEdit");
                                              if (
                                                isEdit === "true" &&
                                                typeof window.savePlantDetails ===
                                                  "function"
                                              ) {
                                                await window.savePlantDetails(
                                                  "continue",
                                                  {
                                                    skipNavigate: true,
                                                    skipLoading: true,
                                                  },
                                                );
                                              }
                                            } finally {
                                              window.__isdModalLoading = false;
                                            }
                                          }
                                        }
                                        setShowISDModal((prev) => {
                                          const newState = [...(prev || [])];
                                          newState[index] = false;
                                          return newState;
                                        });
                                      }}
                                    >
                                      <ElementSelectionDialog
                                        ref={elementDialogRef}
                                        numElements={
                                          trains[train_id]?.passes[
                                            pass_id_index
                                          ]?.stages[index]
                                            ?.num_element_per_pressure_vessel ||
                                          1
                                        }
                                        elementOptions={selectedTrainRONFElements.map(
                                          (opt) => opt.value,
                                        )}
                                        elementOptionDetails={
                                          selectedTrainRONFElements
                                        }
                                        initialValues={
                                          trains[train_id]?.passes[
                                            pass_id_index
                                          ]?.stages[
                                            index
                                          ]?.ro_nf_element_used?.map(
                                            (element) => element ?? "",
                                          ) || []
                                        }
                                        onSelectionChange={(allSelected) => {
                                          setIsISDConfirmEnabled(allSelected);
                                        }}
                                      />
                                    </CustomModal>
                                  );
                                })()}
                            </>
                          ) : (
                            <WPDropdown
                              key={`${pass_id}ro_nf_element_used${index}`}
                              name="ro_nf_element_used"
                              value={getInputVal("ro_nf_element_used", index)}
                              placeholder={t("addNewPlant.ro/nfElements")}
                              onChange={(selectedOption) =>
                                handleSingleElementChange(index, selectedOption)
                              }
                              options={selectedTrainRONFElements}
                              isError={Boolean(roElementError)}
                              helperText={roElementError}
                              allowOrphanValue
                              iconTooltip={
                                !replaceOrphanConfirm.show &&
                                !expiringSoonDialog.show &&
                                selectedElementValue &&
                                hasMissingSelectedOption
                                  ? {
                                      type: FEEDBACK_TYPES.ERROR,
                                      message: t(
                                        "RONFConfiguration.licenseWarning",
                                      ),
                                    }
                                  : !replaceOrphanConfirm.show &&
                                      !expiringSoonDialog.show &&
                                      selectedElementValue &&
                                      hasExpiringSoonWarning
                                    ? {
                                        type: FEEDBACK_TYPES.WARNING,
                                        message: `${t("RONFConfiguration.elementExpiringSoonTitle")}. ${t("RONFConfiguration.elementExpiringSoonDesc")}`,
                                      }
                                    : undefined
                              }
                            />
                          )}
                        </>
                      )}

                      <div className="padding-top-10">
                        <DupontDatePicker
                          id={`${pass_id}installation_year${index}`}
                          selected={
                            getInputVal("installation_year", index) !== ""
                              ? new Date(
                                  getInputVal("installation_year", index),
                                )
                              : ""
                          }
                          dateFormat={t("ValueStrings.DATE_FORMAT_SMALL")}
                          onChange={(value, name) =>
                            onStageInputChanged(
                              index,
                              value,
                              "installation_year",
                            )
                          }
                          startDate={new Date()}
                          maxDate={new Date()}
                          className={"width-fill "}
                          placeholderText={t("ValueStrings.selectDate")}
                          {...DatePickerConfig}
                        />
                      </div>
                    </div>
                  </>
                );
              })()}
            </div>
          ))}
        </div>
      </div>
      {replaceOrphanConfirm.show && (
        <ConfirmationPopup
          header={t("RONFConfiguration.elementUnavailableTitle")}
          type={DIALOG_TYPES.ERROR}
          description={t("RONFConfiguration.elementUnavailableDesc")}
          confirmBtn={t("ValueStrings.replace")}
          cancelBtn={t("ValueStrings.cancel")}
          onClose={handleReplaceOrphanModalClose}
        />
      )}
      {expiringSoonDialog.show && (
        <ConfirmationPopup
          header={t("RONFConfiguration.elementExpiringSoonTitle")}
          type={DIALOG_TYPES.WARNING}
          description={t("RONFConfiguration.elementExpiringSoonDesc")}
          confirmBtn={t("ValueStrings.okay")}
          onClose={handleExpiringSoonDialogClose}
        />
      )}
    </>
  );
};
